// Owned-products API client for the marketplace customer embed — TD-671 Task 8.
//
// Wire shape mirrors the Go wire structs exactly (project rule §39):
//   spec:    api/openapi/components/schemas/owned-products.yaml (OwnedItem & friends)
//   handler: internal/marketplace/handler/customer_handler_owned_products.go
//            (GET /api/v1/marketplace/me/owned-products, gated by view-orders)
//   golden:  handler/testdata/contract/listMyOwnedProducts__mixed.golden.json
//
// The handler emits `{"data": [...]}`, so after embedApiFetch's .data unwrap
// listOwnedProducts() returns OwnedItemWire[] directly. The list is
// UNPAGINATED by contract (one item per subscription plus one per distinct
// one-time product), so there is no cursor or limit to send.
//
// Every key of an item is ALWAYS present; "not applicable" and "hidden by the
// member's permissions" both arrive as null (see the field notes below), so a
// client distinguishes them only through the member's permissions, which the
// shell already holds (`productsAccess`).
//
// Clients MUST ignore an unknown `service_options[].type` / `actions[].type`
// (the spec reserves `serial_numbers` and `renew`); the unions below name only
// the variants that exist today.

import { embedApiFetch } from "../../embed-shared/api-client";
import { idempotencyHeaders } from "../../embed-shared/idempotency-errors";
import type { Subscription } from "../../embed-shared/types";

const OWNED_PRODUCTS_PATH = "/api/v1/marketplace/me/owned-products";

/** Same enum as Order.status. On a one-time item a line is always `shipped` or
 *  `delivered`; on a subscription item it can be any value (F5). */
export type OwnedLineStatus =
  | "pending_payment"
  | "pending"
  | "confirmed"
  | "processing"
  | "shipped"
  | "delivered"
  | "cancelled"
  | "refunded";

/** ownedProductRefResp — from the live catalog; when the product row is gone,
 *  the newest source line's frozen name with `product_type` / `image_url` null. */
export interface OwnedProductRefWire {
  id: string;
  name: string;
  product_type: string | null;
  image_url: string | null;
}

/** ownedLineResp — `quantity` is net of completed returns. */
export interface OwnedLineWire {
  order_id: string;
  order_number: string;
  order_line_id: string;
  status: OwnedLineStatus;
  quantity: number;
  /** RFC 3339, UTC. */
  placed_at: string;
}

/** ownedStoreRefResp. */
export interface OwnedStoreRefWire {
  id: string;
  name: string;
}

/** ownedAssignmentResp — one store's share of a one-time item (quantity >= 1). */
export interface OwnedAssignmentWire {
  store: OwnedStoreRefWire;
  quantity: number;
}

/** serviceOptionResp — the product's app badges and auto-install note. Only the
 *  variants below are rendered; an unknown `type` (e.g. the reserved
 *  `serial_numbers`) is ignored by contract. */
export type ServiceOptionWire =
  | {
      type: "app_download";
      platform: "ios" | "android";
      url: string;
      /** A platform badge the client renders, not an image URL. */
      icon: "app_store" | "google_play";
    }
  | {
      type: "auto_installed";
      platform: "android";
      store: "wiseasy";
      message: string;
    };

/** actionResp — the server's verdict on what the caller may do with the item
 *  (cancel while cancellable, resume while a cancellation can be reversed, and
 *  an API-only help entry that the embed never renders, R13). */
export type ActionWire =
  | { type: "cancel"; placement: "detail"; subscription_id: number }
  | { type: "resume"; placement: "row"; subscription_id: number }
  | {
      type: "help";
      placement: "row";
      product_id: string;
      subscription_id: number | null;
      /** Default content for the client's AI chat (R13). */
      context: string;
    };

/** ownedItemResp. A subscription item carries `quantity_owned`,
 *  `unassigned_quantity` and `assignments` as null; a one-time item carries
 *  `subscription` and `store` as null. */
export interface OwnedItemWire {
  kind: "subscription" | "one_time";
  product: OwnedProductRefWire;
  /** The oldest `placed_at` among `lines`; null only when `lines` is empty. */
  first_purchased_at: string | null;
  last_purchased_at: string | null;
  /** The item's source order lines, newest first. */
  lines: OwnedLineWire[];
  /** Subscription items only; null without manage-subscriptions. */
  subscription: Subscription | null;
  /** Subscription items only; null without manage-stores or when the store is gone. */
  store: OwnedStoreRefWire | null;
  /** One-time items only: units owned, net of completed returns. */
  quantity_owned: number | null;
  /** One-time items only; equals `quantity_owned` without manage-stores. */
  unassigned_quantity: number | null;
  /** One-time items only: stores holding units (quantity > 0), ordered by
   *  store name; null without manage-stores. */
  assignments: OwnedAssignmentWire[] | null;
  service_options: ServiceOptionWire[];
  actions: ActionWire[];
}

/** GET /me/owned-products — requires view-orders; the caller must not send it
 *  without that permission (a 403 would surface as the load-error banner). */
export async function listOwnedProducts(): Promise<OwnedItemWire[]> {
  return (await embedApiFetch<OwnedItemWire[]>(OWNED_PRODUCTS_PATH)) ?? [];
}

/** Request body of POST /me/owned-products/{product_id}/moves. A null id is the
 *  unassigned bucket (the server also accepts the key omitted). */
export interface MoveOwnedUnitsBody {
  from_store_id: string | null;
  to_store_id: string | null;
  quantity: number;
}

/** POST /me/owned-products/{product_id}/moves — requires manage-stores and an
 *  Idempotency-Key (the route is registered with the idempotency middleware, so
 *  an absent key is a 400; rule 79 / F1 client half).
 *
 *  Returns the product's one-time item AFTER the move (the caller re-seeds its
 *  row from it, rule 59). A replay with the same key returns the current item
 *  without moving twice, so a caller retrying the SAME submit passes the SAME
 *  `idempotencyKey`; omitted, a fresh key is generated for this one call. */
export async function moveOwnedProductUnits(
  productId: string,
  body: MoveOwnedUnitsBody,
  idempotencyKey?: string,
): Promise<OwnedItemWire> {
  const headers = idempotencyKey ? { "Idempotency-Key": idempotencyKey } : idempotencyHeaders();
  return embedApiFetch<OwnedItemWire>(`${OWNED_PRODUCTS_PATH}/${encodeURIComponent(productId)}/moves`, {
    method: "POST",
    body,
    headers,
  });
}
